import React, { useState, useEffect } from 'react';
import { Box, Flex, Link, List, ListItem } from '@chakra-ui/react';

const NAV_LINKS = [
  { label: 'Home',         href: '#hero',           id: 'hero'          },
  { label: 'Modularity',   href: '#modular',        id: 'modular'       },
  { label: 'Service',      href: '#field-service',  id: 'field-service' },
  { label: 'Applications', href: '#use-cases',      id: 'use-cases'     },
  { label: 'Contact',      href: '#contact-form',   id: 'contact-form'  },
];

// Maps section element IDs that aren't nav items to their parent nav ID
const SECTION_TO_NAV = {
  assembly: 'modular',
};

function Nav() {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const navTargets = NAV_LINKS.map(({ id }) => document.getElementById(id));
    const extraTargets = Object.keys(SECTION_TO_NAV).map((id) => document.getElementById(id));
    const targets = [...navTargets, ...extraTargets].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActiveId(SECTION_TO_NAV[id] ?? id);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Box as="nav" aria-label="Main navigation"
      position="fixed" top={0} left={0} right={0}
      height="var(--nav-h)" zIndex={200}
      pointerEvents="none">

      <Flex position="relative" zIndex={1} height="100%"
        align="center" justify="space-between"
        maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }} width="100%">

        {/* Logo pill */}
        <Link href="#" display="inline-flex" alignItems="center"
          pointerEvents="auto"
          aria-label="O-ID — return to top"
          px={3} py={2} borderRadius="6px"
          sx={{
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: 0.7, textDecoration: 'none' },
          }}>
          <Box as="img" src="/o-id-logo-with-text.png" alt="O-ID"
            sx={{ height: '22px', width: 'auto' }} />
        </Link>

        {/* Nav links */}
        <List display="flex" gap={2} listStyleType="none" m={0} p={0}>
          {NAV_LINKS.map(({ label, href, id }) => {
            const isActive = activeId === id;
            return (
              <ListItem key={id}>
                <Link
                  href={href}
                  pointerEvents="auto"
                  display="inline-flex" alignItems="center"
                  minH="44px" px={4}
                  fontSize="11px" fontWeight={600}
                  letterSpacing="0.18em" textTransform="uppercase"
                  textDecoration="none"
                  borderRadius="6px"
                  color={isActive ? 'oid.accent' : 'oid.text'}
                  borderBottom="2px solid"
                  borderColor={isActive ? 'oid.accent' : 'transparent'}
                  sx={{
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    transition: 'color 0.2s ease, border-color 0.2s ease',
                    '&:hover': { color: 'oid.accent', textDecoration: 'none' },
                  }}
                  _focusVisible={{ outline: '2px solid', outlineColor: 'oid.accent', outlineOffset: '3px', borderRadius: '6px' }}>
                  {label}
                </Link>
              </ListItem>
            );
          })}
        </List>
      </Flex>
    </Box>
  );
}

export default Nav;
